import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton, Typography } from '@mui/material';

import { formatNumber } from '../../../../../shared/helpers/functions/number';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import { getCurrencySignForAreaUnit } from '../../../../../shared/helpers/functions/currency';
import PromoCodeInput from '../PromoCodeInput';
import { DASH_SYMBOL } from '../../../../../shared/helpers/constants/markup';
import rootStore from '../../../rootStore/RootStore';
import AnnualSubscriptionForm from '../AnnualSubscriptionForm';
import useAnnualSubscriptionPreview from '../../../subscriptionPreview/hooks/useAnnualSubscriptionPreview';

const AnnualSubscriptionDetails = observer(() => {
  const { t } = useTranslation();
  const { uiStateStore, subscriptionPreviewStore, areaUnitStore } = rootStore;
  const {
    subscriptionPreviewGenerated: isPreviewGenerated,
    subscriptionPreviewError: isPreviewError,
  } = subscriptionPreviewStore;
  const { annualPlanValid } = uiStateStore;
  const { areaUnit } = areaUnitStore;
  const { isLoaded: isPriceConfigLoaded, isLoading } =
    usePriceConfig(rootStore);
  const {
    subTotalCost,
    discount,
    tax,
    totalCost,
    getSubscriptionPreviewTable,
  } = useAnnualSubscriptionPreview();
  const currencySign = getCurrencySignForAreaUnit(areaUnit);

  let previewTable;

  if (isPriceConfigLoaded && isPreviewGenerated && annualPlanValid) {
    previewTable = getSubscriptionPreviewTable();
  } else if ((!isPreviewGenerated || isLoading) && !isPreviewError) {
    previewTable = <Skeleton variant="rounded" height={100} />;
  } else {
    previewTable = null;
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <AnnualSubscriptionForm />
      {previewTable}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 2 }}>
        <Typography variant="subtitle2">{t('checkout.subtotal')}</Typography>
        <Typography variant="subtitle2">
          {Number.isNaN(subTotalCost) || !isPreviewGenerated ? (
            DASH_SYMBOL
          ) : (
            <>
              <span>{currencySign}</span>
              <span>{formatNumber(subTotalCost)}</span>
            </>
          )}
        </Typography>
      </Box>
      <PromoCodeInput styles={{ formControl: { width: 1 } }} />
      {discount !== 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 2 }}>
          <Typography variant="subtitle2">{t('checkout.discount')}</Typography>
          <Typography>
            <span>- {currencySign}</span>
            <span>{formatNumber(discount)}</span>
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 2 }}>
        <Typography variant="subtitle2">{t('checkout.tax')}</Typography>
        <Typography>
          {Number.isNaN(tax) || !isPreviewGenerated ? (
            DASH_SYMBOL
          ) : (
            <>
              <span>{currencySign}</span>
              <span>{formatNumber(tax)}</span>
            </>
          )}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 2 }}>
        <Typography variant="subtitle2" sx={{ fontSize: 18 }}>
          {t('plan-select.pricing-example.total')}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={(theme) => ({ fontSize: 18, color: theme.palette.primary.main })}
        >
          {Number.isNaN(subTotalCost) || !isPreviewGenerated ? (
            DASH_SYMBOL
          ) : (
            <>
              <span>{currencySign}</span>
              <span>{formatNumber(totalCost)}</span>
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
});

export default AnnualSubscriptionDetails;
